import { ChangeDetectorRef } from '@angular/core';
import { Selector, Store } from '@ngrx/store';
import { action, observable } from 'mobx-angular';
import { Observable, OperatorFunction, Subject } from 'rxjs';
import * as rx from 'rxjs/operators';

export const selectorsKey = Symbol();
export const destroyKey = Symbol();
export const isNgOnDestroyOverwrittenKey = Symbol();
export const isSelectableKey = Symbol();

interface SelectorsList {
  [key: string]: {
    selector: Selector<any, any>;
    transformer?: (a: Observable<any>) => Observable<any>;
    subscribe: boolean;
    shareReplay: boolean;
    mobxDecorator: any;
  };
}

const setupNgOnDestroy = (target: any) => {
  if (isNgOnDestroyOverwrittenKey in target) {
    return;
  }

  const onDestroy = (target: any) => {
    if (destroyKey in target && typeof (target as any)[destroyKey] === 'function') {
      (target as any)[destroyKey]();
    }
  };

  if ('ngOnDestroy' in target && typeof (target as any)['ngOnDestroy'] === 'function') {
    const previousOnDestroy: () => void = (target as any)['ngOnDestroy'];

    Object.defineProperty(target, 'ngOnDestroy', {
      configurable: true,
      value() {
        previousOnDestroy.bind(this)();
        onDestroy(this);
      },
    });
  } else {
    Object.defineProperty(target, 'ngOnDestroy', {
      configurable: true,
      value() {
        onDestroy(this);
      },
    });
  }

  Object.defineProperty(target, destroyKey, {
    configurable: true,
    value() {},
  });

  Object.defineProperty(target, isNgOnDestroyOverwrittenKey, {
    configurable: true,
    value: true,
  });
};

export function makeSelectable(
  target: any,
  { store$, cdr }: { store$: Store<any>; cdr?: ChangeDetectorRef },
) {
  const destroy$ = new Subject<void>();

  Object.defineProperty(target, destroyKey, {
    configurable: true,
    value() {
      destroy$.next();
      destroy$.complete();
    },
  });

  const selectors = selectorsKey in target ? target[selectorsKey]() : [];

  Object.keys(selectors).forEach(propertyKey => {
    const { mobxDecorator, transformer, shareReplay, selector, subscribe } =
      selectors[propertyKey];

    const pipes: OperatorFunction<any, any>[] = [];

    if (shareReplay) {
      pipes.push(rx.takeUntil(destroy$));
      pipes.push(rx.shareReplay());
    }

    pipes.push(rx.takeUntil(destroy$));

    const value = transformer
      ? transformer(
          store$.select(selector).pipe(...(pipes as [OperatorFunction<any, any>])),
        )
      : store$.select(selector).pipe(...(pipes as [OperatorFunction<any, any>]));

    if (subscribe) {
      if (mobxDecorator) {
        value.subscribe(
          action((x: any) => {
            (target as any)[propertyKey] = x;
          }),
        );
      } else {
        value.subscribe((x: any) => {
          (target as any)[propertyKey] = x;
          cdr?.markForCheck();
        });
      }
    } else {
      (target as any)[propertyKey] = value;
    }
  });

  (target as any)[isSelectableKey] = true;
}

export function Select<T, E = T>(
  selector: Selector<any, T>,
  transformer?: (a: Observable<T>) => Observable<E>,
  shareReplay = true,
) {
  return <P extends string>(target: { [K in P]: Observable<E> }, propertyKey: P) => {
    const selectors: SelectorsList =
      selectorsKey in target ? (target as any)[selectorsKey]() : {};

    selectors[propertyKey] = {
      selector,
      transformer,
      shareReplay,
      mobxDecorator: false,
      subscribe: false,
    };

    setupNgOnDestroy(target);

    Object.defineProperty(target, selectorsKey, {
      configurable: true,
      value() {
        return selectors;
      },
    });
  };
}

export function SelectToMobx<T, E = T>(
  selector: Selector<any, T>,
  {
    mobxDecorator = observable,
    transformer,
  }: {
    mobxDecorator?: PropertyDecorator;
    transformer?: (a: Observable<T>) => Observable<E>;
  } = {},
) {
  return <P extends string>(
    target: { [K in P]?: Readonly<E> },
    propertyKey: P,
    _descriptor?: TypedPropertyDescriptor<T>,
  ) => {
    const selectors: SelectorsList =
      selectorsKey in target ? (target as any)[selectorsKey]() : {};

    mobxDecorator(target, propertyKey);

    selectors[propertyKey] = {
      selector,
      transformer,
      shareReplay: false,
      subscribe: true,
      mobxDecorator,
    };

    setupNgOnDestroy(target);

    Object.defineProperty(target, selectorsKey, {
      configurable: true,
      value() {
        return selectors;
      },
    });
  };
}

export function SelectSubscribe<T, E = T>(
  selector: Selector<any, T>,
  transformer?: (a: Observable<T>) => Observable<E>,
) {
  return <P extends string>(
    target: { [K in P]?: E },
    propertyKey: P,
    _descriptor?: TypedPropertyDescriptor<T>,
  ) => {
    const selectors: SelectorsList =
      selectorsKey in target ? (target as any)[selectorsKey]() : {};

    selectors[propertyKey] = {
      selector,
      transformer,
      shareReplay: false,
      subscribe: true,
      mobxDecorator: false,
    };

    setupNgOnDestroy(target);

    Object.defineProperty(target, selectorsKey, {
      configurable: true,
      value() {
        return selectors;
      },
    });
  };
}
